// src/pages/NotificationsPage.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { colors } from "../theme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useNavigate } from "react-router-dom";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotif, setSelectedNotif] = useState(null);

  // Load notifications from localStorage or API
  useEffect(() => {
    const saved = localStorage.getItem("notifications");
    if (saved) {
      setNotifications(JSON.parse(saved));
    } else {
      const defaultNotifs = [];
      setNotifications(defaultNotifs);
      localStorage.setItem("notifications", JSON.stringify(defaultNotifs));
    }
  }, []);

  const markAsRead = (id) => {
    const updated = notifications.map((n) =>
      n.id === id ? { ...n, unread: false } : n
    );
    setNotifications(updated);
    localStorage.setItem("notifications", JSON.stringify(updated));
  };

  const unreadCount = notifications.filter((n) => n.unread).length;

  // 🔹 Header message logic
  const headerSubtitle =
    notifications.length === 0
      ? "You don't have any notifications yet."
      : unreadCount > 0
      ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
      : "You're all caught up!";

  return (
    <>
      <div className="min-h-screen bg-gray-50 pt-28 pb-12 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-3 bg-white rounded-full shadow hover:bg-gray-50"
              >
                <ArrowBackIcon sx={{ fontSize: 28, color: colors.darkGrey }} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  Notifications
                </h1>
                <p className="text-gray-600">{headerSubtitle}</p>
              </div>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={() => {
                  const allRead = notifications.map((n) => ({
                    ...n,
                    unread: false,
                  }));
                  setNotifications(allRead);
                  localStorage.setItem(
                    "notifications",
                    JSON.stringify(allRead)
                  );
                }}
                className="flex items-center gap-2 px-5 py-3 bg-yellow-500 text-white rounded-xl font-medium hover:bg-yellow-600 transition"
              >
                <MarkEmailReadOutlinedIcon />
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {notifications.length === 0 ? (
              <div className="p-20 text-center">
                <div className="text-2xl text-gray-300 mb-4">
                  No notifications
                </div>
                <p className="text-gray-500">
                  You don't have any notifications yet. Check back later for
                  updates.
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <motion.div
                  key={notif.id}
                  whileHover={{ backgroundColor: "#fffbeb" }}
                  onClick={() => {
                    setSelectedNotif(notif);
                    if (notif.unread) markAsRead(notif.id);
                  }}
                  className={`p-6 border-b last:border-0 cursor-pointer transition ${
                    notif.unread ? "bg-yellow-50" : "bg-white"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-bold text-lg text-gray-800">
                          {notif.title}
                        </h3>
                        {notif.unread && (
                          <span className="px-3 py-1 bg-red-500 text-white text-xs rounded-full font-bold">
                            NEW
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mt-1 line-clamp-2">
                        {notif.message}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">{notif.time}</p>
                    </div>
                    {notif.unread && (
                      <div className="w-3 h-3 bg-red-500 rounded-full ml-4 mt-2"></div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Popup */}
      {selectedNotif && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedNotif(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedNotif.title}
              </h2>
              {!selectedNotif.unread && (
                <span className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-full">
                  Read
                </span>
              )}
            </div>

            <div className="text-gray-700 text-lg leading-relaxed mb-6">
              {selectedNotif.message}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">{selectedNotif.time}</p>
              <button
                onClick={() => setSelectedNotif(null)}
                className="px-8 py-3 bg-yellow-500 text-white rounded-xl font-medium hover:bg-yellow-600 transition"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default NotificationsPage;
